/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useReducer, useState } from "react";
import { If, Then, Else } from "react-if";
import {
  Button,
  FlexContainer,
  SVGIcon,
  Tab,
  Tabs,
  theme,
} from "@nordcloud/gnui";
import { WizardButton, WrapWizardButton } from "~/components";
import { showError } from "~/services/toast";
import { useCreateApplication } from "~/views/applications/hooks/useCreateApplication/useCreateApplication";
import { AfterApplicationCreation } from "./AfterApplicationCreation";
import { ApplicationDetailsForm } from "./ApplicationDetailsForm";
import { ApplicationEnvironments } from "./ApplicationEnvironments";
import { ApplicationWizardCtxProvider } from "./ApplicationProvider";
import { ConfirmAndFinalize } from "./ConfirmAndFinalize";
import { defaultApplicationData } from "./constants";
import { State, ApplicationData } from "./types";

const defaultState = {
  currentStep: 0,
  applicationCreated: false,
  applicationId: "",
};

export function ApplicationCreateWizard() {
  const [state, updateState] = useReducer(
    (data: State, partialData: Partial<State>) => ({ ...data, ...partialData }),
    defaultState
  );

  const [applicationData, setApplicationData] = useState<ApplicationData>(
    defaultApplicationData
  );

  const { createApplication, loading } = useCreateApplication({
    onSuccess: (id) => {
      updateState({ applicationId: id, applicationCreated: true });
    },
  });

  const isNextStepAllowed = state.currentStep < 2;

  const nextStep = () => {
    if (isNextStepAllowed) {
      updateState({ currentStep: state.currentStep + 1 });
    }
  };

  const handleSteps = (key: number) => updateState({ currentStep: key });

  const previousStep = () =>
    updateState({ currentStep: state.currentStep - 1 });

  const handleCreateApplication = () => {
    if (!applicationData.name?.trim()) {
      showError("Missing Application Name");
      return;
    }

    const createApplicationInput = {
      name: applicationData.name.trim(),
      owner: applicationData?.owner?.trim(),
      contacts: applicationData?.contactPerson?.trim()
        ? [applicationData.contactPerson.trim()]
        : [],
      description: applicationData?.description?.trim() ?? undefined,
    };

    createApplication(createApplicationInput);
  };

  const buttons = {
    step1: (
      <WrapWizardButton>
        <WizardButton form="applicationDetailsForm">
          <FlexContainer gap={theme.spacing.spacing02}>
            Next Step
            <SVGIcon name="arrowRight" size="sm" />
          </FlexContainer>
        </WizardButton>
      </WrapWizardButton>
    ),
    step2: (
      <WrapWizardButton>
        <Button icon="arrowLeft" severity="low" onClick={previousStep}>
          Previous Step
        </Button>
      </WrapWizardButton>
    ),
    step3: (
      <WrapWizardButton>
        <Button icon="arrowLeft" severity="low" onClick={previousStep}>
          Previous Step
        </Button>
        <WizardButton
          type="submit"
          onClick={handleCreateApplication}
          disabled={loading}
          initialState={loading ? "loading" : ""}
        >
          Create Application
        </WizardButton>
      </WrapWizardButton>
    ),
  };

  return (
    <ApplicationWizardCtxProvider
      value={{ applicationData, setApplicationData }}
    >
      <Tabs step={state.currentStep} wizard handleTab={handleSteps}>
        <Tab disabled heading="Application Details" buttons={buttons.step1}>
          <ApplicationDetailsForm nextStep={nextStep} />
        </Tab>
        <Tab
          disabled
          heading="Application Environments"
          buttons={buttons.step2}
        >
          <ApplicationEnvironments nextStep={nextStep} />
        </Tab>
        <Tab
          disabled
          heading="Confirm and Finalize"
          buttons={!state.applicationCreated && buttons.step3}
        >
          <If condition={state.applicationCreated}>
            <Then>
              <AfterApplicationCreation
                applicationId={state.applicationId}
                updateState={updateState}
              />
            </Then>
            <Else>
              <ConfirmAndFinalize updateState={updateState} />
            </Else>
          </If>
        </Tab>
      </Tabs>
    </ApplicationWizardCtxProvider>
  );
}
